/**
 * Der Raum vor dem Match (Produktentscheidung vom 05.10.2026).
 *
 * Man landet hier, sobald man ein Match startet - nicht erst, wenn der Gegner
 * da ist. Vorher sass man zuerst auf der leeren Lane und wurde erst beim
 * Eintreffen des Gegners in einen anderen Bildschirm geschoben; jetzt ist es
 * EIN Raum, in dem der Gegner erscheint.
 *
 * Drei Zustaende, derselbe Rahmen:
 *
 * - waiting  - allein. Es wird gesucht, oder der Matchcode steht gross da.
 * - lobby    - beide da. Ready und die festen Kurznachrichten.
 * - starting - beide bereit. Der Countdown laeuft.
 *
 * Ready ist absichtlich gesperrt, solange niemand da ist: Sonst koennte man
 * drueckend weggehen, und man landete wieder in einem Match gegen jemanden,
 * der nicht am Geraet sitzt - genau das, was dieser Raum verhindert.
 *
 * HIER WIRD DER LINEBOUND GEWAEHLT (Produktentscheidung vom 13.10.2026).
 * Vorher reiste die Wahl beim Beitreten mit und stand danach fest. Im
 * Schnellmatch laeuft dafuer eine Minute; wer bis dahin nichts waehlt,
 * bekommt einen zufaelligen.
 *
 * Was dabei bewusst NICHT zu sehen ist: WELCHEN der andere spielt
 * (Produktentscheidung vom 05.10.2026). Nur, OB er schon gewaehlt hat.
 * Wuerde man seinen Pick sehen und danach noch wechseln koennen, entstuende
 * Counterpicking - und unsere ganze Balance ist blind gemessen. Die Spanne
 * von 6,6 Punkten zwischen den Heroes gilt fuer zufaellige Paarungen; bei
 * Gegenpicks zaehlt nur noch die beste Antwort auf den anderen, und die
 * schlechteste Paarung steht bei 61:39.
 *
 * Die Lane laeuft halbdurchsichtig dahinter - man sieht also schon, wo man
 * gleich spielt, statt auf eine Wand zu schauen.
 */

import { useState } from 'react';
import { HEROES, LOBBY_WAIT_SECONDS } from '@dueline/game-data';
import type { LOBBY_SIGNALS } from '@dueline/shared';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { LobbyControls, LobbySignalLine } from './LobbyControls.js';
import { LineboundWahl } from './LineboundWahl.js';
import { lobbyEreignis, type LobbyEreignis } from './lobbyEreignis.js';
import { imWarteraum } from './weltBedarf.js';
import { MenueHintergrund } from './MenueHintergrund.js';

interface Props {
  snapshot: MatchSnapshot;
  onToggleReady: () => void;
  onSignal: (signal: (typeof LOBBY_SIGNALS)[number]) => void;
  onLeave: () => void;
  /** Wahl des eigenen Linebound (13.10.2026). */
  onSelectHero: (heroId: string) => void;
}

/** 42 -> "0:42", 75 -> "1:15". */
function minuten(sekunden: number): string {
  const s = Math.max(0, sekunden);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function Lobby({ snapshot, onToggleReady, onSignal, onLeave, onSelectHero }: Props) {
  const [kopiert, setKopiert] = useState(false);

  const allein = snapshot.phase === 'waiting';
  const laeuftSchon = snapshot.phase === 'starting';
  const code = snapshot.matchCode;

  /*
   * Wurde gewuerfelt? (13.10.2026)
   *
   * Der Server wuerfelt im Augenblick, in dem der Countdown beginnt, und
   * setzt dabei "gewaehlt". Hier wird deshalb der letzte Stand VOR dem
   * Countdown festgehalten: War da noch nichts gewaehlt, ist der Linebound
   * im Countdown ein gewuerfelter - und das soll man erfahren.
   */
  const [warOffen, setWarOffen] = useState(!snapshot.heroChosen);
  // Nachgefuehrt waehrend des Zeichnens, solange der Countdown noch nicht laeuft.
  if (!laeuftSchon && warOffen === snapshot.heroChosen) setWarOffen(!snapshot.heroChosen);
  const gewuerfelt = laeuftSchon && warOffen ? HEROES.find((h) => h.id === snapshot.heroId) : null;

  // Aufgerundet, damit die letzte Sekunde als "1" stehen bleibt und nicht als
  // "0" verschwindet, bevor es losgeht.
  const rest = Math.max(0, Math.ceil(snapshot.phaseSecondsRemaining));

  /*
   * Wer kommt, wer geht (Produktentscheidung vom 15.10.2026).
   *
   * Robin hatte in einer Lobby erlebt, dass der andere ging und nichts
   * davon zu sehen war - die Uhr blieb einfach stehen. Jetzt steht hier
   * eine Zeile, und sie bleibt stehen, bis das Gegenteil passiert: Wer
   * allein wartet, soll noch lesen koennen, warum er allein ist.
   *
   * WAS gemeldet wird, entscheidet lobbyEreignis - eine eigene Regel,
   * damit sie sich ohne Bild pruefen laesst. Nachgefuehrt wird waehrend
   * des Zeichnens (dasselbe Muster wie bei warOffen darueber): Ein
   * Schnappschuss kommt zwanzigmal je Sekunde, und ein Effekt waere ein
   * Umweg ueber einen zweiten Durchgang.
   */
  const [gesehen, setGesehen] = useState<{ gegner: string; meldung: LobbyEreignis | null }>({
    gegner: snapshot.opponent?.sessionId ?? '',
    meldung: null,
  });
  const ereignis = lobbyEreignis(gesehen, {
    gegner: snapshot.opponent?.sessionId ?? '',
    gegnerIstBot: snapshot.opponent?.isBot ?? false,
    imWarteraum: imWarteraum(snapshot),
  });
  if (ereignis.gedaechtnis.gegner !== gesehen.gegner) {
    setGesehen({
      gegner: ereignis.gedaechtnis.gegner,
      meldung: ereignis.ereignis ?? gesehen.meldung,
    });
  }

  const kopieren = () => {
    // Auf manchen Geraeten ist die Zwischenablage gesperrt. Dann bleibt der
    // Code trotzdem lesbar auf dem Bildschirm stehen - deshalb reicht es,
    // den Fehler zu schlucken.
    void navigator.clipboard
      ?.writeText(code)
      .then(() => setKopiert(true))
      .catch(() => setKopiert(false));
  };

  return (
    <div className="dl-lobby dl-lobby--bild">
      {/* Dasselbe Bild wie im Hauptmenue (15.10.2026) - man bleibt im selben Raum. */}
      <MenueHintergrund />
      <div className="dl-lobby__panel">
        {laeuftSchon ? (
          <>
            <div className="dl-panel__title">{t.lobby.starting}</div>
            <div className="dl-lobby__count">{rest}</div>
            {gewuerfelt && (
              <p className="dl-lobby__zufall dl-lobby__zufall--ergebnis">
                {t.lobby.randomAssigned}: <strong>{gewuerfelt.linebound}</strong>{' '}
                <span className="dl-linebound__rolle">{gewuerfelt.name}</span>
              </p>
            )}
          </>
        ) : allein ? (
          <>
            <div className="dl-panel__title">{t.menu.waitingTitle}</div>
            {code ? (
              <>
                {/* Privates Match: Der Code muss gross und lesbar sein -
                    man liest ihn vor oder fotografiert ihn ab. */}
                <div className="dl-waiting__label">{t.menu.waitingCode}</div>
                <div className="dl-waiting__code">{code}</div>
                <p className="dl-waiting__hint">{t.menu.waitingHint}</p>
                <button type="button" className="dl-button dl-lobby__ready" onClick={kopieren}>
                  {kopiert ? t.menu.copied : t.menu.copy}
                </button>
              </>
            ) : (
              <p className="dl-waiting__hint">{t.menu.quickHint}</p>
            )}
          </>
        ) : (
          <>
            <div className="dl-panel__title">{t.lobby.title}</div>
            {/*
              Die Uhr gibt es nur im Schnellmatch - in einer privaten Lobby
              steht sie auf null und bleibt deshalb weg (13.10.2026).
            */}
            {rest > 0 ? (
              /*
                Eine Uhr statt einer nackten Zahl (13.10.2026): was sie
                zaehlt, die Zeit als Minuten und ein Balken, der ablaeuft.
                Die letzten zehn Sekunden werden warm.
              */
              <div
                className={'dl-lobby__uhr' + (rest <= 10 ? ' dl-lobby__uhr--knapp' : '')}
                role="timer"
              >
                <span className="dl-lobby__uhrLabel">{t.lobby.timerLabel}</span>
                <span className="dl-lobby__uhrZeit">{minuten(rest)}</span>
                <span className="dl-lobby__uhrBalken" aria-hidden="true">
                  <span
                    className="dl-lobby__uhrFuellung"
                    style={{ width: `${Math.min(1, rest / LOBBY_WAIT_SECONDS) * 100}%` }}
                  />
                </span>
              </div>
            ) : (
              <p className="dl-waiting__hint">{t.lobby.hint}</p>
            )}
          </>
        )}

        {/*
          Wer gekommen oder gegangen ist (15.10.2026) - in jedem Zustand
          des Warteraums, denn gerade das Alleinsein danach braucht die
          Erklaerung.
        */}
        {gesehen.meldung && (
          <p className="dl-waiting__hint dl-lobby__ereignis" role="status">
            {gesehen.meldung === 'gegnerDa' ? t.lobby.opponentJoined : t.lobby.opponentLeft}
          </p>
        )}

        {/*
          Die Wahl des Linebound (13.10.2026) - gesperrt, sobald der 3-2-1
          laeuft. Sie steht AUCH ALLEIN schon da (Robin, 13.10.2026): Man
          kommt sofort hierher und soll die Wartezeit nutzen koennen,
          statt auf einen Gegner zu warten und dann erst anzufangen.

          Vom Gegner steht hier nur, OB er schon gewaehlt hat; welchen,
          sieht man bewusst nicht.
        */}
        <div className="dl-lobby__wahl">
          <div className="dl-menu__abschnittTitel">{t.menu.heroTitle}</div>
          <LineboundWahl
            gewaehlt={snapshot.heroChosen ? snapshot.heroId : ''}
            onWaehle={onSelectHero}
            gesperrt={laeuftSchon}
          />
          {/*
            Was passiert, wenn man nichts waehlt - vorher sagte es das Spiel
            nirgends. Die Zeile behaelt ihren Platz, auch wenn sie nicht
            gebraucht wird: Sonst schrumpfte der Kasten beim ersten Tippen auf
            einen Linebound (Rueckmeldung vom 15.10.2026).
          */}
          <p
            className={
              'dl-lobby__zufall' +
              (laeuftSchon || snapshot.heroChosen ? ' dl-lobby__zufall--weg' : '')
            }
            aria-hidden={laeuftSchon || snapshot.heroChosen}
          >
            {t.lobby.randomHint}
          </p>
          {!allein && (
            <p className="dl-waiting__hint">
              {snapshot.opponent?.heroChosen ? t.lobby.opponentPicked : t.lobby.opponentPicking}
            </p>
          )}
        </div>

        {/*
          Ready und die Kurznachrichten gibt es erst, wenn jemand da ist.
          Allein waere beides sinnlos: Niemand koennte die Nachricht lesen,
          und ein Ready auf Vorrat ist genau das Versprechen, das dieser
          Raum nicht annehmen will.
        */}
        {!allein && (
          <>
            <LobbyControls snapshot={snapshot} onToggleReady={onToggleReady} onSignal={onSignal} />
            <LobbySignalLine snapshot={snapshot} />
          </>
        )}

        {/*
          Verlassen bleibt immer moeglich - es gibt bewusst keine Zeitgrenze
          fuers Bereitsein. Wer nicht warten will, geht.
        */}
        <button type="button" className="dl-button dl-lobby__leave" onClick={onLeave}>
          {t.menu.leave}
        </button>
      </div>
    </div>
  );
}
